import { useState, useMemo, useEffect, useRef, startTransition } from "react";
import { motion, AnimatePresence } from "motion/react";
import { OrganizationProjectsQuery } from "../../__generated__/graphql";
import useOrganizationProjects from "../../pages/projects/hooks/useOrganizationProjects";
import Checkbox from "../Checkbox";
import { cn } from "../../lib/utils";
import { ChevronUpDownIcon, CubeIcon } from "@heroicons/react/24/outline";

type Project = OrganizationProjectsQuery["projects"][number];

interface ProjectSelectListProps {
  onSelect: (selectedProjects: Project[]) => void;
  className?: string;
}

export default function ProjectSelectList({
  onSelect,
  className,
}: ProjectSelectListProps) {
  const { data, loading } = useOrganizationProjects();

  const allProjects = useMemo(() => {
    return (data?.projects ?? []).filter(
      (project): project is Project => project !== null
    );
  }, [data]);

  // Sort projects alphabetically by name
  const sortedProjects = useMemo(() => {
    return [...allProjects].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );
  }, [allProjects]);

  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(
    new Set()
  );
  const prevSelectedProjectIdsRef = useRef<Set<string>>(new Set());

  // Filter projects based on search query
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return sortedProjects;

    const query = searchQuery.toLowerCase().trim();
    return sortedProjects.filter(
      (project) =>
        project.name.toLowerCase().includes(query) ||
        (project.description?.toLowerCase().includes(query) ?? false)
    );
  }, [sortedProjects, searchQuery]);

  // Notify parent when selection changes
  useEffect(() => {
    // Only update if selection actually changed
    const prevIds = prevSelectedProjectIdsRef.current;
    const hasChanged =
      prevIds.size !== selectedProjectIds.size ||
      Array.from(selectedProjectIds).some((id) => !prevIds.has(id)) ||
      Array.from(prevIds).some((id) => !selectedProjectIds.has(id));

    if (hasChanged) {
      const selectedProjects = sortedProjects.filter((project) =>
        selectedProjectIds.has(project.id)
      );
      // Use startTransition to make this update non-blocking
      startTransition(() => {
        onSelect(selectedProjects);
      });
      prevSelectedProjectIdsRef.current = new Set(selectedProjectIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectIds, sortedProjects]);

  const handleProjectToggle = (projectId: string) => {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className={cn("p-4 text-secondary text-sm", className)}>
        Chargement...
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div
        className="flex items-center justify-between text-sm text-primary cursor-pointer hover:text-secondary transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <CubeIcon className="w-4 h-4" />
          <div className="text-sm">Filtrer par projet</div>
        </div>
        <ChevronUpDownIcon className="w-4 h-4" />
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="flex flex-col gap-2">
              {/* Search input */}
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 text-xs w-full p-2 rounded-sm bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border placeholder:text-tertiary"
              />

              {/* Project list */}
              <div className="flex flex-col gap-1 max-h-96 overflow-y-auto custom-scrollbar">
                {filteredProjects.length === 0 ? (
                  <div className="p-4 text-tertiary text-sm text-center">
                    Aucun projet trouvé
                  </div>
                ) : (
                  filteredProjects.map((project) => {
                    const isSelected = selectedProjectIds.has(project.id);

                    return (
                      <div
                        key={project.id}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-card/40 transition-colors select-none"
                        )}
                        onClick={() => handleProjectToggle(project.id)}
                      >
                        <Checkbox checked={isSelected} />
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="font-medium text-sm text-primary truncate">
                            {project.name}
                          </span>
                          {project.description && (
                            <span className="text-xs text-secondary truncate">
                              {project.description}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
